<!-- Overlay backdrop para desktop -->
<div
  *ngIf="showSuggestions() && !breakpointService.isMobile()"
  class="fixed inset-0 bg-black/25 search-overlay z-40 animate-in fade-in-0 duration-300"
  (click)="closeSearchOverlay()"
  aria-hidden="true"
></div>

<div
  class="relative"
  [class]="divStyleClass"
  [class.search-bar-elevated]="showSuggestions() && !breakpointService.isMobile()"
>
  <!-- Botón de búsqueda -->
  <button
    type="button"
    class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-800 transition-colors"
    [class]="buttonStyleClass"
    (click)="onSearchSubmit()"
    aria-label="Buscar productos"
  >
    <lucide-icon
      [name]="searchIcon"
      [class]="iconStyleClass + ' w-auto h-4'"
      aria-hidden="true"
    ></lucide-icon>
  </button>

  <!-- Campo de búsqueda principal -->
  <input
    type="text"
    [placeholder]="placeholder"
    class="pl-10 w-full outline-none focus:outline-1 focus:outline-gray-300 px-4 py-3 border-0 rounded-xl bg-gray-200 xl:bg-white hover:bg-gray-200 text-gray-700 placeholder:text-gray-500 focus:bg-gray-100 h-10 xl:h-12 transition-all duration-200 font-light"
    [value]="searchValue()"
    (input)="onSearchChange($event)"
    (focus)="onInputFocus()"
    (blur)="onInputBlur()"
    (keydown.enter)="onSearchSubmit()"
    (keydown.escape)="closeSearchOverlay()"
    autocomplete="off"
    role="combobox"
    [attr.aria-expanded]="showSuggestions() && filteredCategories().length > 0"
    [attr.aria-haspopup]="'listbox'"
    [attr.aria-autocomplete]="'list'"
    [attr.aria-controls]="showSuggestions() && filteredCategories().length > 0 ? 'search-suggestions' : null"
    [attr.aria-activedescendant]="null"
  />

  <!-- Sugerencias flotantes -->
  <ng-container *ngIf="showSuggestions() && filteredCategories().length > 0 && !breakpointService.isMobile()">
    <ng-container *ngTemplateOutlet="suggestionsDropdown"></ng-container>
  </ng-container>
</div>

<!-- Templates reutilizables -->
<ng-template #suggestionsDropdown>
  <div
    id="search-suggestions"
    class="absolute top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto animate-in fade-in-0 slide-in-from-top-2 duration-200 search-bar-dropdown"
    [class.in-sticky-header]="isInStickyHeader"
    [class]="isInStickyHeader ? 'left-0 right-0 xl:left-0 xl:right-0' : 'left-0 right-0'"
    (mouseenter)="onSuggestionsMouseEnter()"
    (mouseleave)="onSuggestionsMouseLeave()"
    role="listbox"
    [attr.aria-label]="'Sugerencias de búsqueda'"
  >
    <div class="py-2">
      <!-- Header de resultados -->
      <ng-container *ngTemplateOutlet="resultsHeader"></ng-container>
      
      <!-- Lista de categorías -->
      <div class="py-1" role="list">
        <ng-container
          *ngFor="let category of filteredCategories(); trackBy: trackByCategory"
        >
          <ng-container *ngTemplateOutlet="categoryItem; context: { $implicit: category }"></ng-container>
        </ng-container>
      </div>
      
      <!-- Footer de sugerencias -->
      <ng-container *ngTemplateOutlet="suggestionsFooter"></ng-container>
    </div>
  </div>
</ng-template>

<ng-template #resultsHeader>
  <div class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 bg-gray-50 rounded-t-xl">
    <div class="flex items-center justify-between">
      <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Repuestos encontrados</h3>
      <div class="flex items-center gap-2">
        <span class="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-xl">
          {{ filteredCategories().length }} resultado{{ filteredCategories().length !== 1 ? "s" : "" }}
        </span>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #categoryItem let-category>
  <div
    class="px-4 py-3 hover:bg-blue-50 transition-all duration-150 group"
    role="option"
    [attr.aria-selected]="false"
  >
    <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between items-stretch gap-3">
      <!-- Información de la categoría -->
      <div
        class="flex-1 cursor-pointer border-r border-gray-100 pr-3"
        (click)="onSuggestionClick(category)"
        tabindex="0"
        role="button"
        [attr.aria-label]="'Ver repuestos de ' + smartTitle(category.name)"
      >
        <div class="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
          {{ smartTitle(category.name) }}
        </div>
      </div>

      <!-- Controles del carrito -->
      <div class="flex items-center gap-2 pl-3">
        <ng-container *ngIf="isLeafCategory(category); else parentCategoryControls">
          <ng-container *ngTemplateOutlet="cartControls; context: { $implicit: category }"></ng-container>
        </ng-container>
        <ng-template #parentCategoryControls>
          <ng-container *ngTemplateOutlet="parentCategoryButton; context: { $implicit: category }"></ng-container>
        </ng-template>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #cartControls let-category>
  <div class="flex items-center gap-2">
    <!-- Controles de cantidad -->
    <div class="flex items-center bg-gray-100 rounded-lg p-1" role="group" aria-label="Control de cantidad">
      <button
        type="button"
        (click)="decrementVisualQuantity(category.id); onControlClick($event)"
        class="w-7 h-7 bg-white hover:bg-gray-200 text-gray-600 rounded-md transition-colors duration-200 flex items-center justify-center"
        [class.opacity-50]="getVisualQuantity(category.id) <= 1"
        [disabled]="getVisualQuantity(category.id) <= 1"
        aria-label="Disminuir cantidad"
      >
        <lucide-icon [name]="minusIcon" class="w-3 h-3" aria-hidden="true"></lucide-icon>
      </button>

      <div class="flex-1 text-center min-w-[2rem]" aria-live="polite">
        <span class="text-sm font-medium text-gray-900">{{ getVisualQuantity(category.id) }}</span>
      </div>

      <button
        type="button"
        (click)="incrementVisualQuantity(category.id); onControlClick($event)"
        class="w-7 h-7 bg-white hover:bg-gray-200 text-gray-600 rounded-md transition-colors duration-200 flex items-center justify-center"
        aria-label="Aumentar cantidad"
      >
        <lucide-icon [name]="plusIcon" class="w-3 h-3" aria-hidden="true"></lucide-icon>
      </button>
    </div>

    <!-- Botón de carrito -->
    <button
      type="button"
      (click)="addToCart(category); onControlClick($event)"
      class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 font-medium text-xs"
      [class]="isInCart(category.id) ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-500 text-white hover:bg-blue-700'"
      [attr.aria-label]="isInCart(category.id) ? 'Producto en carrito' : 'Agregar al carrito'"
    >
      <lucide-icon [name]="cartIcon" class="w-3 h-3" aria-hidden="true"></lucide-icon>
      <span>{{ isInCart(category.id) ? "En carrito" : "Agregar" }}</span>
      <span *ngIf="isInCart(category.id)" class="text-xs">({{ getVisualQuantity(category.id) }})</span>
    </button>
  </div>
</ng-template>

<ng-template #parentCategoryButton let-category>
  <div class="flex items-center">
    <button
      type="button"
      (click)="onSuggestionClick(category); onControlClick($event)"
      class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 font-medium text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
      [attr.aria-label]="'Ver repuestos de ' + smartTitle(category.name)"
    >
      <svg
        class="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        ></path>
      </svg>
      <span>Ver repuestos</span>
    </button>
  </div>
</ng-template>

<ng-template #suggestionsFooter>
  <div class="px-4 py-3 text-xs text-gray-400 border-t border-gray-100 bg-gray-50 rounded-b-xl">
    <div class="flex items-center justify-between">
      <span>Presiona Enter para buscar productos</span>
      <span class="text-blue-500">Búsqueda inteligente activa</span>
    </div>
  </div>
</ng-template>
